<template>
  <div class="container-fluid">
    <div v-if="ballotData" class="back-button">
      <router-link :to="`/oscar-pool/${club_id}`">
        <i class="fa fa-angle-double-left" aria-hidden="true"></i> Return to Oscar Pool
      </router-link>
    </div>

    <div v-if="ballotData" class="card mol-center-card-width ballot-card">

      <div ref="floating-selections-made" class="selections-made floating hidden">
        <AchievementRing
            class="selections-made-achievement-ring"
            :total_segments="numVotesTotal"
            :filled_segments="numVotesCast"
            size="30px"
            :center_img="achievementRingCenterImg"
        ></AchievementRing>
        <div class="selections-made-text">{{ numVotesCast }} of {{ numVotesTotal }} selections chosen</div>
      </div>

      <div ref="card-header" class="card-header">
        <div class="club-title"><ClubPicture :club="clubData.club" size="18"></ClubPicture> {{ clubData.club.name }}</div>
        <h1>OSCAR POOL BALLOT</h1>
        <div class="ceremony-number-and-year">{{ ceremonyOrdinalText }} Ceremony • {{ ceremonyDateText }}</div>
        <div>Honoring achievements in</div>
        <div>{{ ballotData.films.length }} films from {{ ceremonyYearsHonoredText }}</div>

        <OscarPoolCountdown
            class="oscar-pool-countdown"
            :club_id="club_id"
            :force_lock="!votingEnabled"
            @locked="countdownLocked = true"
            @unlocked="countdownLocked = false"
        ></OscarPoolCountdown>

        <div class="selections-made">
          <AchievementRing
              class="selections-made-achievement-ring"
              :total_segments="numVotesTotal"
              :filled_segments="numVotesCast"
              size="30px"
              :center_img="achievementRingCenterImg"
          ></AchievementRing>
          <div class="selections-made-text">{{ numVotesCast }} of {{ numVotesTotal }} selections chosen</div>
        </div>
      </div>

      <div class="card-body">
        <table v-for="(category, index) in ballotData.categories" :key="category" class="category-table">
          <thead>
            <tr class="category-header-row">
              <th class="category-table-name">{{category.name}}</th>
              <th class="category-table-points"><div>{{category.points}}</div><div class="points-text">pts</div></th>
              <th v-if="secondChoiceEnabled" class="category-table-points category-table-second-choice-points">
                <div v-if="index===0" class="second-choice-description">
                  Second guess for<br>fewer points<br><i class="fa fa-angle-down" aria-hidden="true"></i>
                </div>
                <div>{{category.points_second_choice}}</div><div class="points-text">pts</div>
              </th>
            </tr>
          </thead>

          <tbody>

            <div v-if="!votingEnabled && noChoicesMadeForCategory(category)" class="no-choices-selected-message">No Choices Selected</div>

            <template v-for="nomination in sortedNominationData(category)" :key="nomination.nom_id">

              <tr v-if="votingEnabled || ballotData.my_ballot.choices.includes(nomination.nom_id) || (secondChoiceEnabled && ballotData.my_ballot.second_choices.includes(nomination.nom_id))">
                <td class="category-table-name">
                  <span v-if="getCategoryType(category.name) === 'acting'">
                    <span class="nominee-title">{{nomination.nominee}}</span><br><FilmLink :film_name="film_name(nomination.film_id)" :film_id="nomination.film_id" :no_strikethrough="true"></FilmLink>
                  </span>
                  <span v-else-if="getCategoryType(category.name) === 'song'">
                    <span class="nominee-title">"{{nomination.song_title}}"</span> <span class="song-nom-from">from</span> <FilmLink :film_name="film_name(nomination.film_id)" :film_id="nomination.film_id" :no_strikethrough="true"></FilmLink><br>{{nomination.nominee}}<br>
                  </span>
                  <span v-else>
                    <span class="nominee-title"><FilmLink :film_name="film_name(nomination.film_id)" :film_id="nomination.film_id" :no_strikethrough="true"></FilmLink></span><br>{{nomination.nominee}}
                  </span>
                </td>

                <td class="category-table-points">
                  <BallotBox
                      :ref="`ballot-box-${nomination.nom_id}-1`"
                      :selected="ballotData.my_ballot.choices.includes(nomination.nom_id)"
                      :disabled="!votingEnabled || ballotData.my_ballot.second_choices.includes(nomination.nom_id)"
                      @checked="updateBallotChoice(nomination.nom_id, true, false)"
                      @unchecked="updateBallotChoice(nomination.nom_id, false, false)">
                  </BallotBox>
                </td>

                <td class="category-table-points category-table-second-choice-points" v-if="secondChoiceEnabled">
                  <BallotBox
                      :ref="`ballot-box-${nomination.nom_id}-2`"
                      :selected="ballotData.my_ballot.second_choices.includes(nomination.nom_id)"
                      :disabled="!votingEnabled || ballotData.my_ballot.choices.includes(nomination.nom_id)"
                      :smaller="true"
                      @checked="updateBallotChoice(nomination.nom_id, true, true)"
                      @unchecked="updateBallotChoice(nomination.nom_id, false, true)">
                  </BallotBox>
                </td>

              </tr>

            </template>
          </tbody>

        </table>
      </div>
    </div>

    <div v-if="ballotData" class="back-button">
      <router-link :to="`/oscar-pool/${club_id}`">
        <i class="fa fa-angle-double-left" aria-hidden="true"></i> Return to Oscar Pool
      </router-link>
    </div>

  </div>

</template>


<script>
import axios from "axios";
import {Film, Nomination} from "@/film";
import analytics from "@/analytics";
import AchievementRing from "@/components/achievements/AchievementRing.vue";
import {nextTick} from "vue";
import FilmLink from "@/components/FilmLink.vue";
import BallotBox from "@/components/clubs/oscar pool/BallotBox.vue";
import ClubPicture from "@/components/clubs/ClubPicture.vue";
import OscarPoolCountdown from "@/components/clubs/oscar pool/OscarPoolCountdown.vue";

const dateFormatObj = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});

export default {
  name: "OscarPoolBallotPage",
  components: {OscarPoolCountdown, ClubPicture, BallotBox, FilmLink, AchievementRing},
  props: ['club_id', 'oscar_pool_id'],
  data() {
    return {
      countdownLocked: true,
      ceremonyState: null,
      userBallot: [],
      categoryNominationMap: new Map(),
      ballotData: null,
      clubData: null,
      countdownTextUpdateInterval: null,
      countdownText: null,
      cardHeaderObserver: null
    }
  },
  computed: {
    votingEnabled() {
      return ( (this.ceremonyState === 'pre-ceremony') && !this.countdownLocked );
    },
    ceremonyOrdinalText() {
      return Film.getCeremonyOrdinalText(this.ballotData.ceremony_info.ceremony);
    },
    ceremonyDateText() {
      return dateFormatObj.format(Film.getDateOfCeremony(this.ballotData.ceremony_info.ceremony));
    },
    ceremonyYearsHonoredText() {
      return Film.ceremonyYearsHonoredText(this.ballotData.ceremony_info.ceremony);
    },
    categoryList() {
      return Film.getNominatedCategoriesForFilms(this.filmList).map(c => c.name);
    },
    achievementRingCenterImg() {
      return ( this.numVotesTotal === this.numVotesCast ) ?
          'achievements/ballot-box.svg' : 'achievements/disabled/ballot-box.svg';
    },
    secondChoiceEnabled() {
      return this.ballotData.second_choice_enabled;
    },
    numVotesTotal() {
      return this.ballotData.categories.length * (this.secondChoiceEnabled? 2 : 1);
    },
    numVotesCast() {
      return this.ballotData.my_ballot.choices.length + (this.secondChoiceEnabled? this.ballotData.my_ballot.second_choices.length : 0);
    }
  },
  async created() {

    // eslint-disable-next-line
    return axios.get(`/api/club/${this.club_id}/oscar-pool/my-ballot/${this.oscar_pool_id}`)
        .then(response => {

          //console.log('GET oscar-pool/my-ballot', response.data);
          // console.log('oscar-pool/my-ballot response headers', response.headers);
          this.clubData = response.data.club_data;

          this.ballotData = response.data.ballot_data;
          this.ballotData.ceremony_info.ceremony_start_time_utc = new Date(this.ballotData.ceremony_info.ceremony_start_time_utc);
          this.ceremonyState = response.data.ceremony_state;

          return nextTick();
        })
        .then(() => {
          this.cardHeaderObserver = new IntersectionObserver(this.onCardHeaderIntersection);
          this.cardHeaderObserver.observe(this.$refs['card-header']);
        })
        .catch(err => {
          //console.log('err', err);

          // unauthorized? kick the user back to the club profile page
          if ( err.response?.status === 401 ) {
            this.$router.push({name: 'OscarPoolProfilePage', params: {club_id: this.club_id}});
            return;
          }

          analytics.notifyBugsnagOfError(err);
        });
  },
  mounted() {
  },
  unmounted() {
    this.cardHeaderObserver?.disconnect();

    if ( this.countdownTextUpdateInterval ) {
      clearInterval(this.countdownTextUpdateInterval);
      this.countdownTextUpdateInterval = null;
    }
  },
  methods: {

    noChoicesMadeForCategory(_category) {

      const myFirstChoices = this.ballotData.my_ballot.choices;
      const mySecondChoices = this.ballotData.my_ballot.second_choices;

      return !_category.nomination_data.some(_nom => {
        return (
            myFirstChoices.includes(_nom.nom_id) ||
            (this.secondChoiceEnabled && mySecondChoices.includes(_nom.nom_id))
        );
      });

    },

    sortedNominationData(_category) {

      const sortByBallotChoices = (nom1, nom2) => {

        // first, push first-choices up
        const myFirstChoices = this.ballotData.my_ballot.choices;
        if ( myFirstChoices.includes(nom1.nom_id) ) {
          return -1;
        }
        if ( myFirstChoices.includes(nom2.nom_id) ) {
          return 1;
        }

        // then, push second-choices up
        const mySecondChoices = this.ballotData.my_ballot.second_choices;
        if ( mySecondChoices.includes(nom1.nom_id) ) {
          return -1;
        }
        if ( mySecondChoices.includes(nom2.nom_id) ) {
          return 1;
        }

        return 0;
      };

      const getNomineeSortString = _nom => {

        // actor? return name as last-name-comma-first
        if ( Nomination.isActingCategory(_category.name) ) {
          const nameParts = _nom.nominee.trim().split(' ');
          const lastName = nameParts.pop();
          return `${lastName}, ${nameParts.join(' ')}`.toLowerCase();
        }

        // song title? normalize the titles by stripping leading "the", "a" or "an"
        if ( Nomination.isSongCategory(_category.name) ) {
          return _nom.song_title.replace(/^the\s+/i, '').replace(/^a\s+/i, '').replace(/^an\s+/i, '').toLowerCase();
        }

        // film title? normalize the titles by stripping leading "the", "a" or "an"
        return this.film_name(_nom.film_id).replace(/^the\s+/i, '').replace(/^a\s+/i, '').replace(/^an\s+/i, '').toLowerCase();
      };


      const sortAlphabetically = (nom1, nom2) => {
        const titleNom1 = getNomineeSortString(nom1);
        const titleNom2 = getNomineeSortString(nom2);

        if ( titleNom1 < titleNom2 ) {
          return -1;
        }
        if ( titleNom2 < titleNom1 ) {
          return 1;
        }
        return 0;
      };

      return this.votingEnabled?
          _category.nomination_data.sort(sortAlphabetically) :
          _category.nomination_data.sort(sortByBallotChoices);
    },

    getCategoryType(_categoryName) {
      return Nomination.isActingCategory(_categoryName) ? 'acting' : Nomination.isSongCategory(_categoryName) ? 'song' : 'other';
    },

    film_name(_film_id) {
      return this.ballotData.films.find(f => (f._id === _film_id))?.name;
    },

    async updateBallotChoice(_nominationId, _select, _secondChoice=false) {

      const ballotBoxComponent = this.$refs[`ballot-box-${_nominationId}-${_secondChoice ? '2' : '1'}`][0];
      const previousCheckState = ballotBoxComponent.checked;

      const endpoint = _select ? 'choose-nomination' : 'clear-nomination';

      return axios.post(`/api/club/${this.club_id}/oscar-pool/my-ballot/${endpoint}`, {
        nomination_id: _nominationId,
        second_choice: _secondChoice
      })
          .then(response => {
            //console.log(`POST oscar-pool/my-ballot/${endpoint}`, response.data);
            this.ballotData.my_ballot = response.data;
            ballotBoxComponent.setCheckboxState(_select);
          })
          .catch(err => {
            console.log(err);
            ballotBoxComponent.setCheckboxState(previousCheckState);
          })
    },

    onCardHeaderIntersection(_entries) {

      const entry = _entries[0];
      if ( !entry ) {
        return;
      }

      if ( entry.isIntersecting ) {
        //console.log(`visible`, entry)
        this.$refs['floating-selections-made']?.classList.add('hidden');
      } else {
        //console.log(`NOT visible`, entry)
        this.$refs['floating-selections-made']?.classList.remove('hidden');
      }

    },
  }
}
</script>


<style scoped>

.container-fluid {
  padding-top: 20px;
  background-color: var(--mol-color-gold-background);

  .back-button {
    text-align: center;
    padding-bottom: 1.5em;

    a {
      color: var(--mol-color-gold-text);
      text-decoration: inherit;
    }
  }

}

.ballot-card {

  background-color: var(--mol-color-gold-background);
  overflow: hidden;
  margin-bottom: 20px;
  padding: 5px;

  border: solid;
  border-width: 3px;
  border-color: var(--mol-color-gold);
  border-radius: 0.375rem;

  .card-header {
    background-color: inherit;
    text-align: center;
    width: 90%;
    font-size: smaller;
    margin: 1em auto 0 auto;
    padding: 0;
    border-style: none;

    .club-title {
      font-size: 1.1rem;
    }

    .ceremony-number-and-year {
      font-weight: bold;
      font-size: large;
    }
  }

  .second-choice-description {
    position: absolute;
    opacity: 80%;

    text-transform: uppercase;
    /*color: var(--mol-color-gold-text);*/
    font-size: 70%;
    font-weight: normal;
    line-height: 1.125em;
    right: 20px;
    transform: translateY(-4.2cap);
    text-align: right;
    .fa {
      font-size: 150%;
      transform: translateY(-0.3cap) translateX(-14px);
      /*animation: pulse-animation 1s infinite;*/
    }
  }

  .card-body {

    .category-table {
      margin-top: 2em;

      table-layout: fixed;
      width: 100%;

      .points-text {
        line-height: 0.75em;
        font-size: 70%;
        margin-bottom: 0.75em;
      }

      .category-table-points {
        width: 35px;
        text-align: center;

        &.category-table-second-choice-points {
          font-size: 90%;
          /*color: slateblue;*/

          sup {
            font-weight: normal;
          }
        }

      }

      tbody {

        font-size: smaller;
        td {
          padding-top: 0.5em;
          padding-bottom: 0.5em;
        }

        .nominee-title {
          font-weight: 500;
        }

        .song-nom-from {
          font-style: italic;
        }
      }
    }
  }



  .selections-made {
    margin-top: 1em;

    .selections-made-achievement-ring {
      display: inline-block;
      margin-right: 1em;
    }
    .selections-made-text {
      font-size: larger;
      font-weight: 500;
      color: var(--mol-color-gold-text);
      display: inline-block;
      margin-top: 3px;
    }

    &.floating {

      position: fixed;
      top: var(--mol-navbar-height);
      right: 0;
      left: 0;
      
      margin-left: auto;
      margin-right: auto;
      width: fit-content;

      z-index: 4;
      box-shadow: 0px 0px 20px 1px lightgrey;

      border-radius: 10px;
      border-color: lightgrey;
      border-style: solid;
      border-width: thin;
      padding: 5px 15px 5px 15px;

      background-color: var(--mol-color-gold-background);
      animation: fadeIn 1s;

      &.hidden {
        visibility: hidden;
        opacity: 0;
      }
    }
  }

}

@keyframes pulse-animation {
  0% { opacity: 100%; }
  50% { opacity: 40%; }
  100% { opacity: 100%; }
}

.oscar-pool-countdown {
  margin-top: 1em;
  color: var(--bs-secondary);
}

.no-choices-selected-message {
  margin-top: 1em;
  margin-left: 1em;
  font-style: italic;
}

.category-header-row {
  border-style: none none solid none;
  border-width: thin;
  border-color: var(--bs-body-color);
}

</style>
